import isEqual from "lodash/isEqual";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { StringParam, useQueryParams } from "use-query-params";
import { useOnClickOutside } from "../../util/hooks";
import type { IIsOutOfView } from "../../util/util";
import { isOutOfViewport, useMediaQueries } from "../../util/util";
import { TertiaryButtonSmall } from "../Buttons/Buttons";
import type { ChipType } from "../Chips/Chips";
import { Modal } from "../Modal/Modal";
import { PrefilledSelectAllChips } from "../PrefilledSelectAllChips/PrefilledSelectAllChips";
import { DropDownModalWrapper, DropDownWrapper } from "./filter_by.utils";
import ReactDOM from "react-dom";

interface IFilterBy {
  setter: ({ values, filter }: { values: ChipType[]; filter: string }) => void;
  options: ChipType[];
  header: string;
  filter: string;
  label: string;
  preselectedFilters: ChipType[];
  isLastVisible?: boolean;
  usePortal?: boolean;
}

/**
 * Reads in filters from the URL if they exist and applies them to the chips.
 * selectedFilters will update when a user clicks on a chip, but they will only be
 * applied if "apply" or "clear" are clicked. When that array changes the request will run.
 */
export const FilterBy = ({
  setter,
  options,
  header,
  filter,
  label,
  preselectedFilters,
  isLastVisible,
  usePortal = false,
}: IFilterBy) => {
  const [showDropDown, setShowDropDown] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<ChipType[]>(
    preselectedFilters || []
  );
  const [appliedFilters, setAppliedFilters] = useState<ChipType[]>(
    preselectedFilters || []
  );
  const [showModal, setShowModal] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [query, setQuery] = useQueryParams({
    active_tag_view: StringParam,
  });
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [ButtonViewport, setButtonViewport] = useState<
    IIsOutOfView | undefined
  >(isOutOfViewport(buttonRef));
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  const handleFilterClick = (activeChips: ChipType[]) => {
    setSelectedFilters([...activeChips]);
  };

  const getButtonText = useCallback(() => {
    const optionNamesSet = new Set(options.map((opt) => opt.name));
    const validAppliedFilters = appliedFilters.filter((filter) =>
      optionNamesSet.has(filter.name)
    );

    if (validAppliedFilters.length === 0) {
      return `${label}`;
    } else {
      return `${label} (${validAppliedFilters.length})`;
    }
  }, [appliedFilters, label, options]);
  const [buttonText, setButtonText] = useState(getButtonText);

  const handleClickOutside = () => {
    setButtonText(getButtonText());

    if (!isEqual(appliedFilters, selectedFilters)) {
      // When the user clicks outside the modal without applying or clearing
      // and there are changes present
      // reset the selected filters with the applied filters.
      setSelectedFilters(appliedFilters);
      setButtonText(getButtonText());
    }
    setShowDropDown(false);
  };

  useEffect(() => {
    setButtonText(getButtonText());
  }, [appliedFilters, getButtonText]);

  useOnClickOutside(wrapperRef, () => handleClickOutside());

  const apply = () => {
    setAppliedFilters(selectedFilters);
    setter({ values: selectedFilters, filter: filter });
    setButtonText(getButtonText());
    setShowDropDown(false);
  };

  const clear = useCallback(() => {
    // useCallback is needed here because this a dep of the
    // below useEffect
    setSelectedFilters([]);
    setAppliedFilters([]);
    setQuery({
      [filter]: query.active_tag_view,
    });
    setter({ values: [], filter: filter });
    setShowDropDown(false);
  }, [setter, filter, query.active_tag_view, setQuery]);

  useEffect(() => {
    setSelectedFilters(preselectedFilters);
    setAppliedFilters(preselectedFilters);
  }, [preselectedFilters]);

  useEffect(() => {
    if (!showDropDown || !usePortal) return;

    const updatePosition = () => {
      const rect = buttonRef.current?.getBoundingClientRect();
      const dropdownWidth = 500;
      const windowWidth = window.innerWidth;

      if (rect) {
        let left = rect.left + window.scrollX;

        if (rect.left + dropdownWidth > windowWidth) {
          left = rect.right - dropdownWidth + window.scrollX;
        }

        setDropdownPosition({
          top: rect.bottom + window.scrollY,
          left,
        });
      }
    };

    updatePosition();

    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [showDropDown, usePortal]);

  React.useEffect(() => {
    const handleResize = () => setButtonViewport(isOutOfViewport(buttonRef));
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const { isMediumScreen } = useMediaQueries();
  if (!isMediumScreen) {
    return (
      <DropDownWrapper>
        <TertiaryButtonSmall
          active={appliedFilters.length > 0 || showDropDown}
          ref={buttonRef}
          onClick={() => {
            setShowDropDown(!showDropDown);
            setButtonViewport(isOutOfViewport(buttonRef));
          }}
          className="button-item"
          style={{
            opacity: isLastVisible ? "0" : "1",
            pointerEvents: isLastVisible ? "none" : "auto",
          }}
        >
          {buttonText}
        </TertiaryButtonSmall>
        {showDropDown &&
          (usePortal ? (
            ReactDOM.createPortal(
              <DropDownModalWrapper
                ref={wrapperRef}
                className="dropdownWrapperDiv"
                isOutOfViewport={ButtonViewport}
                style={{
                  position: "absolute",
                  top: `${dropdownPosition.top}px`,
                  left: `${dropdownPosition.left}px`,
                  pointerEvents: "auto",
                }}
              >
                <PrefilledSelectAllChips
                  allChips={options}
                  handleClick={handleFilterClick}
                  header={header}
                  withSelectAll={true}
                  selectedChips={selectedFilters}
                  ToggleLabel={label}
                  handleApply={apply}
                  handleClear={clear}
                  scroll={true}
                />
              </DropDownModalWrapper>,
              document.getElementById("filter-dropdown-portal")!
            )
          ) : (
            <DropDownModalWrapper
              ref={wrapperRef}
              className="dropdownWrapperDiv"
              isOutOfViewport={ButtonViewport}
            >
              <PrefilledSelectAllChips
                allChips={options}
                handleClick={handleFilterClick}
                header={header}
                withSelectAll={true}
                selectedChips={selectedFilters}
                ToggleLabel={label}
                handleApply={apply}
                handleClear={clear}
                scroll={true}
              />
            </DropDownModalWrapper>
          ))}
      </DropDownWrapper>
    );
  } else {
    return (
      <DropDownWrapper>
        <TertiaryButtonSmall
          active={appliedFilters.length > 0}
          onClick={() => {
            setShowModal(true);
          }}
        >
          {buttonText}
        </TertiaryButtonSmall>
        <Modal show={showModal} closeModal={() => setShowModal(false)}>
          <PrefilledSelectAllChips
            allChips={options}
            handleClick={handleFilterClick}
            header={header}
            withSelectAll={true}
            selectedChips={selectedFilters}
            ToggleLabel={label}
            handleApply={() => {
              apply();
              setShowModal(false);
            }}
            handleClear={() => {
              clear();
              setShowModal(false);
            }}
          />
        </Modal>
      </DropDownWrapper>
    );
  }
};
